import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

import {
  PracticeBatterSingleSessionSummaryChartConfigDto,
  PracticeBatterSingleSessionSummaryChartDataDto,
  PraticeBatterSessionSummaryChartRowDto,
} from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import useReportStore from 'hooks/PracticeReport/BatterSingle/reportStore';
import PitchTypeTableCell from 'components/Table/PitchTypeTableCell';

export type PracticeBatterSingleSessionSummaryChartColumn = keyof PraticeBatterSessionSummaryChartRowDto;

const PracticeBatterSingleSessionSummaryChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<PracticeBatterSingleSessionSummaryChartDataDto, PracticeBatterSingleSessionSummaryChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<PracticeBatterSingleSessionSummaryChartColumn | '' | 'id'>('');

  const { selectedConfgValues } = useReportStore();

  const rows = useMemo(
    () =>
      chartData?.rows.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.rows]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<PracticeBatterSingleSessionSummaryChartColumn>(chartConfig.visibleStats);
    const pitchTypeColumnNameKey = selectedConfgValues.pitchGrouping === 'Types' ? 'pitchType' : 'parentPitchType';

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: column === 'pitchType' ? t(pitchTypeColumnNameKey) : t(column),
      headerShortName: column === 'pitchType' ? t('short.' + pitchTypeColumnNameKey) : t('short.' + column),
      sortable: true,
      renderCell: column === 'pitchType' ? (cellValue) => <PitchTypeTableCell pitchType={cellValue} /> : undefined,
    }));
  }, [chartConfig.visibleStats, selectedConfgValues.pitchGrouping]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default PracticeBatterSingleSessionSummaryChart;
